/**
 * LoggingErrorBoundary
 *
 * React Error Boundary that captures rendering errors and logs them
 * through the structured FrontendLogger. Replaces unhandled React
 * crashes with a recoverable UI + structured error reporting.
 */

import React from 'react';
import { FrontendLogger, createFrontendLogger } from '../FrontendLogger';

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  logger?: FrontendLogger;
  componentName?: string;
}

interface State {
  hasError: boolean;
}

export class LoggingErrorBoundary extends React.Component<Props, State> {
  private readonly logger: FrontendLogger;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
    this.logger = props.logger ?? createFrontendLogger('storefront');
  }

  static getDerivedStateFromError(_error: Error): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.logger.error(
      `React error boundary caught error in ${this.props.componentName ?? 'unknown'}`,
      error,
      {
        componentStack: errorInfo.componentStack ?? 'unavailable',
        componentName: this.props.componentName ?? 'unknown',
      },
    );
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return React.createElement(
        'div',
        {
          role: 'alert',
          style: {
            padding: '16px',
            border: '1px solid #fca5a5',
            borderRadius: '8px',
            backgroundColor: '#fef2f2',
            color: '#991b1b',
          },
        },
        React.createElement('h3', { style: { margin: '0 0 8px 0' } }, 'Something went wrong'),
        React.createElement(
          'p',
          { style: { margin: 0, fontSize: '14px' } },
          'Please try refreshing the page. If the problem persists, contact support.',
        ),
      );
    }

    return this.props.children;
  }
}
